import { throttle } from '../../../utils/throttle'

/**
 * Head-to-Head (h2h) Table Component
 */
export class GMXH2HTable extends HTMLElement {
	borderRowColEls: HTMLTableCellElement[] = []
	private numCols: number
	private colIds: string[]

	private boundWindowResizeHandler = throttle(this._handleWindowResize.bind(this), 250, { leading: true, trailing: true })

	connectedCallback(): void {
		this.numCols = this.querySelectorAll('thead tr:first-child td').length

		this.addIdsToCols()

		if (this.numCols >= 3) {
			try {
				this._buildDropdowns()
			} catch {
				window.console.error('Error building dropdowns for table. Please ensure the table structure is correct and includes the necessary elements for dropdown functionality.')
			}
		}

		window.addEventListener('resize', this.boundWindowResizeHandler)
	}

	disconnectedCallback(): void {
		window.removeEventListener('resize', this.boundWindowResizeHandler)
	}

	/**
	 * Handles window resize events to determine if dropdowns should be shown or hidden based on the current screen size and number of columns in the table.
	 * @private
	 */
	private _handleWindowResize(): void {
		this._checkIfDropdownsNeeded()

		this.borderRowColEls.forEach(colEl => {
			colEl.colSpan = this.isMobile() ? 2 : 3
		})
	}

	/**
	 * Adds data attributes to each cell in the table to identify their column index, and initializes the colIds array based on the first row of header cells.
	 */
	addIdsToCols(): void {
		const rows = this.querySelectorAll('tr')

		rows.forEach((row) => {
			const cells = row.querySelectorAll('td')
			cells.forEach((cell, cellIndex) => {
				cell.setAttribute('data-col-id', cellIndex.toString())
			})
		})

		this.colIds = Array.from(this.querySelectorAll('thead tr:first-child td')).map((cell) => cell.getAttribute('data-col-id'))
	}

	/**
	 * Determines if the current screen size is considered "mobile" based on a media query. This method is used to decide whether to show dropdowns for column selection in the table.
	 */
	isMobile(): boolean {
		return !window.matchMedia('screen and (min-width: 481px)').matches
	}

	private _checkIfDropdownsNeeded(): void {
		if (this.numCols >= 3 ) {
			if (!this.isMobile() && this.numCols === 3) {
				this.classList.remove('show-dropdowns')
			} else {
				this.classList.add('show-dropdowns')
				this.updateDropdowns()
			}
		}
	}

	private _buildDropdowns(): void {
		const headerCols = [...this.querySelectorAll('thead tr td.header-title')]
		headerCols.shift() // Remove the first header column (assumed to be the row header)
		const headerTitles = Array.from(headerCols).map((col) => col.textContent || '')

		headerCols.forEach((col, index) => {
			const headerTitleEl = col

			const newDropdownEl = document.createElement('div')
			newDropdownEl.classList.add('header-dropdown')

			const newDropdownBtnEl = document.createElement('button') as HTMLButtonElement
			newDropdownBtnEl.classList.add('gmx-btn', 'gmx-btn-link-primary', 'icon-right', 'sort-button-label')
			newDropdownBtnEl.type = 'button'
			newDropdownBtnEl.setAttribute('data-icon-name', 'chevron')
			newDropdownBtnEl.setAttribute('data-icon-dir', 'down')
			newDropdownBtnEl.setAttribute('data-target', `gmx-table-flyout-${index}`)
			newDropdownBtnEl.textContent = headerTitles[index]

			newDropdownEl.appendChild(newDropdownBtnEl)

			const newFlyoutEl = document.createElement('gmx-flyout')
			newFlyoutEl.classList.add('gmx-flyout-dropdown')
			newFlyoutEl.id = `gmx-table-flyout-${index}`
			newFlyoutEl.setAttribute('align', 'right')
			newFlyoutEl.setAttribute('close-on-selection', 'true')

			const flyoutListEl = document.createElement('ul')
			headerTitles.forEach((title, idx) => {
				const listItemEl = document.createElement('li')
				const itemButtonEl = document.createElement('button')
				const isCurrent = title === headerTitles[index]
				itemButtonEl.classList.add('gmx-btn', 'gmx-btn-secondary')
				itemButtonEl.type = 'button'
				itemButtonEl.setAttribute('data-selected-item-id', `${idx + 1}`)
				itemButtonEl.setAttribute('aria-current', isCurrent ? 'true' : 'false')
				if (isCurrent) {
					itemButtonEl.setAttribute('disabled', '')
				}
				itemButtonEl.textContent = title
				listItemEl.appendChild(itemButtonEl)
				flyoutListEl.appendChild(listItemEl)
			})
			newFlyoutEl.appendChild(flyoutListEl)

			newFlyoutEl.addEventListener('click', (e: Event) => {
				const target = e.target as HTMLElement

				if (target.hasAttribute('data-selected-item-id')) {
					const newSelectedValue = target.getAttribute('data-selected-item-id')

					const flyoutColNum = newFlyoutEl.querySelector('li button[aria-current="true"]')?.getAttribute('data-selected-item-id')

					// swap the current column with the new column in the colIds array
					const i = this.colIds.indexOf(flyoutColNum)
					const j = this.colIds.indexOf(newSelectedValue)

					const temp = this.colIds[i]
					this.colIds[i] = this.colIds[j]
					this.colIds[j] = temp

					this.reorderTable(this.colIds)
				}
			})

			newDropdownEl.appendChild(newFlyoutEl)

			headerTitleEl.appendChild(newDropdownEl)
		})

		this._checkIfDropdownsNeeded()
	}

	reorderTable(activeTrims: string[]): void {
		this.querySelectorAll('tr:has(td[data-col-id])').forEach(tr => {
			const cells = [...tr.children]
			const reorderedCells = activeTrims.map(trimId =>
				cells.find((td: HTMLElement) => td.dataset.colId === trimId)
			).filter(Boolean) // Filter out any unmatched cells
			tr.replaceChildren(...reorderedCells) // Append reordered cells
		})

		this.updateDropdowns()
	}

	updateDropdowns(): void {
		const activeCols = [...this.colIds].splice(1, this.isMobile() ? 1 : 2)

		this.querySelectorAll('gmx-flyout li button').forEach(buttonEl => {
			const buttonColId = buttonEl.getAttribute('data-selected-item-id')

			if (activeCols.includes(buttonColId)) {
				buttonEl.classList.add('selected')
			} else {
				buttonEl.classList.remove('selected')
			}
		})
	}
}

if (!customElements.get('gmx-h2h-table')) {
	customElements.define('gmx-h2h-table', GMXH2HTable)
}
